import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrendingDown, TrendingUp, X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

interface TakeProfitStopLossModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSetTakeProfit: (price: number) => void;
  onSetStopLoss: (price: number) => void;
  currentPrice?: number;
  position?: {
    symbol: string;
    side: string;
    entryPrice: number;
    size: number;
    markPrice?: number;
    pnl?: number;
    isolatedMargin?: number;
    leverage?: number;
    isLong?: boolean;
  };
  mode?: 'paper' | 'live';
}

const computeProjection = (
  entryPrice: number,
  targetPrice: number,
  quantity: number,
  isLong: boolean,
  isolatedMargin?: number
) => {
  if (!Number.isFinite(entryPrice) || !Number.isFinite(targetPrice) || !Number.isFinite(quantity)) {
    return { pnl: 0, roi: 0 };
  }

  const absQty = Math.abs(quantity);
  const pnl = isLong
    ? (targetPrice - entryPrice) * absQty
    : (entryPrice - targetPrice) * absQty;
  const roi = isolatedMargin && isolatedMargin > 0 ? (pnl / isolatedMargin) * 100 : 0;
  return { pnl, roi };
};

export const TakeProfitStopLossModal: React.FC<TakeProfitStopLossModalProps> = ({
  isOpen,
  onClose,
  onSetTakeProfit,
  onSetStopLoss,
  currentPrice = 0,
  position,
  mode = 'paper'
}) => {
  useTranslation();
  const [activeTab, setActiveTab] = useState<'takeProfit' | 'stopLoss'>('takeProfit');
  const [takeProfitPrice, setTakeProfitPrice] = useState('');
  const [stopLossPrice, setStopLossPrice] = useState('');
  const [takeProfitPercentage, setTakeProfitPercentage] = useState('');
  const [stopLossPercentage, setStopLossPercentage] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTakeProfitPrice('');
      setStopLossPrice('');
      setTakeProfitPercentage('');
      setStopLossPercentage('');
      setActiveTab('takeProfit');
      setValidationError('');
    }
  }, [isOpen]);

  useEffect(() => {
    setValidationError('');
  }, [takeProfitPrice, stopLossPrice]);

  const isActuallyLong = useMemo(() => {
    if (!position) return true;
    if (typeof position.isLong === 'boolean') return position.isLong;

    const side = position.side?.toUpperCase();
    if (side === 'LONG') return true;
    if (side === 'SHORT') return false;
    return side === 'BUY';
  }, [position]);

  const entryPrice = position?.entryPrice ?? 0;
  const effectiveCurrentPrice = position?.markPrice || currentPrice || entryPrice;
  const isolatedMargin = position?.isolatedMargin;

  const calculatePriceFromPercentage = (percentage: string, isTakeProfit: boolean) => {
    if (!percentage || !entryPrice) return '';
    const percent = parseFloat(percentage);
    if (Number.isNaN(percent)) return '';

    const targetPrice = isTakeProfit
      ? (isActuallyLong ? entryPrice * (1 + percent / 100) : entryPrice * (1 - percent / 100))
      : (isActuallyLong ? entryPrice * (1 - percent / 100) : entryPrice * (1 + percent / 100));

    return targetPrice.toFixed(2);
  };

  const calculatePercentageFromPrice = (price: string, isTakeProfit: boolean) => {
    if (!price || !entryPrice) return '';
    const targetPrice = parseFloat(price);
    if (Number.isNaN(targetPrice)) return '';

    const percentage = isTakeProfit
      ? (isActuallyLong ? ((targetPrice - entryPrice) / entryPrice) * 100 : ((entryPrice - targetPrice) / entryPrice) * 100)
      : (isActuallyLong ? ((entryPrice - targetPrice) / entryPrice) * 100 : ((targetPrice - entryPrice) / entryPrice) * 100);

    return Math.abs(percentage).toFixed(2);
  };

  const validateTakeProfitPrice = (price: number): boolean => {
    if (!position) return false;
    if (!effectiveCurrentPrice || effectiveCurrentPrice <= 0) {
      setValidationError('Current price unavailable');
      return false;
    }

    if (isActuallyLong) {
      if (price <= effectiveCurrentPrice) {
        setValidationError(`Take Profit must be above current market price for LONG positions (TP: $${price.toFixed(2)}, Current: $${effectiveCurrentPrice.toFixed(2)})`);
        return false;
      }
    } else if (price >= effectiveCurrentPrice) {
      setValidationError(`Take Profit must be below current market price for SHORT positions (TP: $${price.toFixed(2)}, Current: $${effectiveCurrentPrice.toFixed(2)})`);
      return false;
    }

    setValidationError('');
    return true;
  };

  const validateStopLossPrice = (price: number): boolean => {
    if (!position) return false;
    if (!effectiveCurrentPrice || effectiveCurrentPrice <= 0) {
      setValidationError('Current price unavailable');
      return false;
    }

    if (isActuallyLong) {
      if (price >= effectiveCurrentPrice) {
        setValidationError(`Stop Loss must be below current market price for LONG positions (SL: $${price.toFixed(2)}, Current: $${effectiveCurrentPrice.toFixed(2)})`);
        return false;
      }
    } else if (price <= effectiveCurrentPrice) {
      setValidationError(`Stop Loss must be above current market price for SHORT positions (SL: $${price.toFixed(2)}, Current: $${effectiveCurrentPrice.toFixed(2)})`);
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleTakeProfitPercentageChange = (value: string) => {
    setTakeProfitPercentage(value);
    requestAnimationFrame(() => {
      setTakeProfitPrice(calculatePriceFromPercentage(value, true));
    });
  };

  const handleTakeProfitPriceChange = (value: string) => {
    setTakeProfitPrice(value);
    if (Number.isNaN(parseFloat(value))) {
      setValidationError('Enter a valid number.');
      setTakeProfitPercentage('');
      return;
    }
    requestAnimationFrame(() => {
      setTakeProfitPercentage(calculatePercentageFromPrice(value, true));
    });
  };

  const handleStopLossPercentageChange = (value: string) => {
    setStopLossPercentage(value);
    requestAnimationFrame(() => {
      setStopLossPrice(calculatePriceFromPercentage(value, false));
    });
  };

  const handleStopLossPriceChange = (value: string) => {
    setStopLossPrice(value);
    if (Number.isNaN(parseFloat(value))) {
      setValidationError('Enter a valid number.');
      setStopLossPercentage('');
      return;
    }
    requestAnimationFrame(() => {
      setStopLossPercentage(calculatePercentageFromPrice(value, false));
    });
  };

  const takeProfitProjection = useMemo(() => {
    const targetPrice = parseFloat(takeProfitPrice);
    if (!position || Number.isNaN(targetPrice) || targetPrice <= 0) return null;
    return computeProjection(entryPrice, targetPrice, position.size, isActuallyLong, isolatedMargin);
  }, [entryPrice, isActuallyLong, isolatedMargin, position, takeProfitPrice]);

  const stopLossProjection = useMemo(() => {
    const targetPrice = parseFloat(stopLossPrice);
    if (!position || Number.isNaN(targetPrice) || targetPrice <= 0) return null;
    return computeProjection(entryPrice, targetPrice, position.size, isActuallyLong, isolatedMargin);
  }, [entryPrice, isActuallyLong, isolatedMargin, position, stopLossPrice]);

  const handleSetTakeProfit = () => {
    const price = parseFloat(takeProfitPrice);
    if (!Number.isNaN(price) && price > 0 && validateTakeProfitPrice(price)) {
      onSetTakeProfit(price);
      onClose();
    }
  };

  const handleSetStopLoss = () => {
    const price = parseFloat(stopLossPrice);
    if (!Number.isNaN(price) && price > 0 && validateStopLossPrice(price)) {
      onSetStopLoss(price);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold">Set Take Profit / Stop Loss</h2>
            {mode === 'live' ? (
              <span className="ml-2 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Live Mode</span>
            ) : (
              <span className="ml-2 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Paper Mode</span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {position && (
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-400">
            {position.symbol} • {isActuallyLong ? 'LONG' : 'SHORT'} • Entry: ${position.entryPrice.toFixed(2)}
            {effectiveCurrentPrice > 0 && <span> • Current: ${effectiveCurrentPrice.toFixed(2)}</span>}
          </div>
        )}

        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('takeProfit')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'takeProfit'
                ? 'text-green-600 border-b-2 border-green-600 bg-green-50 dark:bg-green-900/20'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Take Profit
          </button>
          <button
            onClick={() => setActiveTab('stopLoss')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'stopLoss'
                ? 'text-red-600 border-b-2 border-red-600 bg-red-50 dark:bg-red-900/20'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <TrendingDown className="w-4 h-4" />
            Stop Loss
          </button>
        </div>

        {mode === 'live' && (
          <div className="mx-6 mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              You are setting TP/SL for a live position. Changes affect real funds.
            </p>
          </div>
        )}

        <div className="p-6">
          {activeTab === 'takeProfit' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tp-percentage">Percentage (%)</Label>
                <Input id="tp-percentage" type="number" placeholder="e.g., 5" value={takeProfitPercentage} onChange={(e) => handleTakeProfitPercentageChange(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tp-price">Price ($)</Label>
                <Input id="tp-price" type="number" placeholder="e.g., 45000" value={takeProfitPrice} onChange={(e) => handleTakeProfitPriceChange(e.target.value)} />
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 text-xs text-muted-foreground">
                <div>Projected PnL: {takeProfitProjection ? `${takeProfitProjection.pnl >= 0 ? '+' : ''}$${takeProfitProjection.pnl.toFixed(2)}` : '--'}</div>
                <div className="mt-1">Projected ROI: {takeProfitProjection ? `${takeProfitProjection.roi >= 0 ? '+' : ''}${takeProfitProjection.roi.toFixed(2)}%` : '--'}</div>
              </div>
              {validationError && <div className="text-sm text-red-600 dark:text-red-400 mt-2">{validationError}</div>}
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
                <Button
                  onClick={handleSetTakeProfit}
                  disabled={!takeProfitPrice || parseFloat(takeProfitPrice) <= 0 || Number.isNaN(parseFloat(takeProfitPrice)) || !!validationError}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Set Take Profit
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sl-percentage">Percentage (%)</Label>
                <Input id="sl-percentage" type="number" placeholder="e.g., 3" value={stopLossPercentage} onChange={(e) => handleStopLossPercentageChange(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sl-price">Price ($)</Label>
                <Input id="sl-price" type="number" placeholder="e.g., 42000" value={stopLossPrice} onChange={(e) => handleStopLossPriceChange(e.target.value)} />
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 text-xs text-muted-foreground">
                <div>Projected PnL: {stopLossProjection ? `${stopLossProjection.pnl >= 0 ? '+' : ''}$${stopLossProjection.pnl.toFixed(2)}` : '--'}</div>
                <div className="mt-1">Projected ROI: {stopLossProjection ? `${stopLossProjection.roi >= 0 ? '+' : ''}${stopLossProjection.roi.toFixed(2)}%` : '--'}</div>
              </div>
              {validationError && <div className="text-sm text-red-600 dark:text-red-400 mt-2">{validationError}</div>}
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
                <Button
                  onClick={handleSetStopLoss}
                  disabled={!stopLossPrice || parseFloat(stopLossPrice) <= 0 || Number.isNaN(parseFloat(stopLossPrice)) || !!validationError}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Set Stop Loss
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TakeProfitStopLossModal;
